import React, { useCallback, useEffect, useRef, useState } from "react"
import QRCode from "qrcode"

import Button from "@material-ui/core/Button"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import CircularProgress from "@material-ui/core/CircularProgress"
import Typography from "@material-ui/core/Typography"
import { makeStyles } from "@material-ui/core/styles"

import { RegistrarLogErro } from "../../../../services/clientefiel"
import {
    CancelarPedido,
    ObterStatusPedido,
    ObterStatusPedidoPorUuid,
} from "../../../../services/cliente"
import { useSnackbar } from "../../../../utils/notificacoes"

const STATUS_AGUARDANDO_PAGAMENTO = 77
const INTERVALO_POLLING_MS = 5000

/**
 * O backend monta o StatusPedido com String.valueOf(...), então campos nulos chegam
 * como a string "null". Normaliza para vazio antes de usar.
 */
const normalizar = valor =>
    typeof valor === "string" && valor !== "null" && valor.trim() !== "" ? valor : ""

export default function AguardandoPagamentoPix({ open, pedido, onConfirmado, onDesistir }) {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()

    // A cobrança Pix (qrCodeText/qrCodeUrl) NÃO vem no retorno de CadastrarPedido/
    // AtualizarStatusPedido — esses devolvem a entidade Pedido, que não tem esses campos.
    // Quem devolve é cliente/ObterStatusPedido (entidade StatusPedido).
    const [statusPedido, setStatusPedido] = useState(null)
    const [qrCodeSrc, setQrCodeSrc] = useState("")
    const [carregando, setCarregando] = useState(false)
    const [erro, setErro] = useState("")
    const [cancelando, setCancelando] = useState(false)

    const confirmadoRef = useRef(false)

    // onConfirmado costuma ser uma arrow inline no pai (nova a cada render). Guardar em ref
    // mantém consultarStatus estável — senão os efeitos abaixo remontariam o setInterval a
    // cada render e o painel entraria em loop de requisições.
    const onConfirmadoRef = useRef(onConfirmado)
    onConfirmadoRef.current = onConfirmado

    const aplicativoDados = JSON.parse(localStorage.getItem("aplicativoCF") || "null")

    const qrCodeText = normalizar(statusPedido?.qrCodeText)
    const qrCodeUrl = normalizar(statusPedido?.qrCodeUrl)

    const idPedido = pedido?.id
    const uuidPedido = pedido?.uuid

    const consultarStatus = useCallback(
        async ({ silencioso } = {}) => {
            if (idPedido == null && !uuidPedido) return
            if (confirmadoRef.current) return

            setCarregando(true)
            try {
                const resposta =
                    idPedido != null
                        ? await ObterStatusPedido(idPedido, aplicativoDados)
                        : await ObterStatusPedidoPorUuid(uuidPedido, aplicativoDados)

                if (!resposta || resposta.retornoErro) {
                    const mensagem = resposta?.mensagem ?? "Não foi possível consultar o pedido."
                    setErro(mensagem)
                    if (!silencioso) enqueueSnackbar(mensagem, { variant: "error" })
                    return
                }

                setErro("")
                setStatusPedido(resposta)

                if (resposta.status !== STATUS_AGUARDANDO_PAGAMENTO) {
                    confirmadoRef.current = true
                    onConfirmadoRef.current(resposta)
                    return
                }

                if (!silencioso) {
                    enqueueSnackbar("Ainda não identificamos o pagamento.", { variant: "info" })
                }
            } finally {
                setCarregando(false)
            }
        },
        // aplicativoDados vem do localStorage e é reconstruído a cada render — fora das deps de propósito.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [idPedido, uuidPedido, enqueueSnackbar]
    )

    // Primeira consulta assim que o modal abre: é ela que traz o QR Code.
    useEffect(() => {
        if (!open || (idPedido == null && !uuidPedido)) return
        confirmadoRef.current = false
        setStatusPedido(null)
        setQrCodeSrc("")
        setErro("")
        consultarStatus({ silencioso: true })
    }, [open, idPedido, uuidPedido, consultarStatus])

    useEffect(() => {
        if (!open || (idPedido == null && !uuidPedido)) return
        const id = setInterval(() => {
            consultarStatus({ silencioso: true })
        }, INTERVALO_POLLING_MS)
        return () => clearInterval(id)
    }, [open, idPedido, uuidPedido, consultarStatus])

    useEffect(() => {
        if (!qrCodeText) {
            setQrCodeSrc("")
            return
        }
        if (qrCodeUrl) {
            setQrCodeSrc(qrCodeUrl)
            return
        }
        QRCode.toDataURL(qrCodeText, { width: 220, margin: 1 }, (err, url) => {
            if (err) {
                RegistrarLogErro(err, "AguardandoPagamentoPix.gerarQrCode")
                setQrCodeSrc("")
                return
            }
            setQrCodeSrc(url)
        })
    }, [qrCodeText, qrCodeUrl])

    const handleCopiar = () => {
        navigator.clipboard.writeText(qrCodeText)
        enqueueSnackbar("Código Pix copiado!", { variant: "info" })
    }

    const handleDesistir = async () => {
        if (!pedido) return
        if (!window.confirm("Deseja cancelar este pedido e escolher outra forma de pagamento?")) {
            return
        }
        setCancelando(true)
        try {
            const resposta = await CancelarPedido(
                {
                    id: pedido.id,
                    token: `${process.env.REACT_APP_CLIENTEFIEL_TOKEN}`,
                    appNome: aplicativoDados?.appNome,
                },
                aplicativoDados
            )

            if (resposta?.retornoErro) {
                enqueueSnackbar(resposta.mensagem, { variant: "error" })
                return
            }

            confirmadoRef.current = true
            onDesistir()
        } catch (error) {
            RegistrarLogErro(error, "AguardandoPagamentoPix.handleDesistir", pedido)
            enqueueSnackbar("Não foi possível cancelar o pedido. Tente novamente.", {
                variant: "error",
            })
        } finally {
            setCancelando(false)
        }
    }

    const semCobranca = !carregando && !qrCodeText

    return (
        <Dialog open={!!open} maxWidth="xs" fullWidth>
            <DialogTitle>Aguardando pagamento via Pix</DialogTitle>
            <DialogContent className={classes.conteudo}>
                {qrCodeSrc && <img src={qrCodeSrc} alt="QR Code Pix" className={classes.qrCode} />}

                {!qrCodeSrc && carregando && <CircularProgress />}

                {semCobranca ? (
                    <Typography variant="body2" color="error" className={classes.texto}>
                        {erro ||
                            "Não foi possível gerar a cobrança Pix deste pedido. Tente novamente ou escolha outra forma de pagamento."}
                    </Typography>
                ) : (
                    <Typography variant="body2" className={classes.texto}>
                        Peça para o cliente escanear o QR Code ou copiar o código abaixo no app do
                        banco dele.
                    </Typography>
                )}

                {qrCodeText && (
                    <React.Fragment>
                        <Typography variant="body2" className={classes.codigo}>
                            {qrCodeText}
                        </Typography>

                        <Button
                            onClick={handleCopiar}
                            variant="outlined"
                            className={classes.botaoCopiar}
                        >
                            Copiar código
                        </Button>
                    </React.Fragment>
                )}

                <Typography variant="caption" className={classes.textoSecundario}>
                    Assim que o pagamento for identificado, o pedido é confirmado automaticamente.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDesistir} disabled={cancelando} color="secondary">
                    Alterar forma de pagamento
                </Button>
                <Button
                    onClick={() => consultarStatus({ silencioso: false })}
                    disabled={carregando}
                    variant="contained"
                    color="primary"
                >
                    Já recebi o pagamento
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const useStyles = makeStyles(theme => ({
    conteudo: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: theme.spacing(1),
        paddingBottom: theme.spacing(2),
    },
    qrCode: {
        width: 220,
        height: 220,
    },
    texto: {
        marginTop: theme.spacing(1),
    },
    codigo: {
        wordBreak: "break-all",
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(1),
        borderRadius: 4,
        fontSize: 12,
    },
    botaoCopiar: {
        marginTop: theme.spacing(1),
    },
    textoSecundario: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
}))
